import React, { useCallback, useState, useEffect, useRef } from "react";
import { MoveFocusInside } from "react-focus-lock";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SlideLeftRightTransition, ResizeTransition } from "@activecollab/components";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import { canSeeTimeEstimates } from "~/store/selectors/selector.users.jsx";
import Angie from "~/utils/angie.jsx";
import { StartMenu } from "./StartMenu/StartMenu.jsx";
import {
  SelectTaskDate,
  SelectTaskList,
  SelectTaskAssignee,
  SelectTaskLabels,
  Submenu,
  SelectTaskEstimate,
} from "./Submenus/index.jsx";

const width = 260;

export const TaskOptions = ({
  id,
  taskListId: defaultTaskListId,
  assigneeId,
  labels: defaultLabels,
  estimate,
  jobTypeId,
  hiddenFromClients,
  important,
  startOn,
  dueOn,
  isSplit,
  onChange,
  initialHeight,
  projectId,
  selectDateMode,
  hideFromClients,
  isLoggedUserClient,
  showTaskEstimatesToClients,
  onEscape,
  ...rest
}) => {
  const { t } = useTranslation();
  const canSeeEstimates = useSelector((state) => canSeeTimeEstimates(state, projectId));
  const [menu, setMenu] = useState("index");
  const [enteredMenu, setEnteredMenu] = useState("index");
  const [state, setState] = useState(() => ({
    assignee_id: assigneeId,
    dates: {
      from: startOn,
      to: dueOn,
    },
    estimate,
    job_type_id: jobTypeId,
    is_hidden_from_clients: hiddenFromClients,
    is_important: important,
  }));

  const taskListRef = useRef({
    oldTaskListId: defaultTaskListId,
    newTaskListId: defaultTaskListId,
  });
  const [taskListId, setTaskListId] = useState(defaultTaskListId);

  const labelsRef = useRef({
    oldLabels: defaultLabels,
    newLabels: defaultLabels,
  });
  const [labels, setLabels] = useState(() => defaultLabels);

  const [isLeft, setIsLeft] = useState(true);
  const [height, setHeight] = useState(() => {
    if ((isLoggedUserClient && !showTaskEstimatesToClients) || (!hideFromClients && !canSeeEstimates))
      return initialHeight - 64;
    if ((isLoggedUserClient && showTaskEstimatesToClients) || !hideFromClients || !canSeeEstimates)
      return initialHeight - 32;
    return initialHeight;
  });

  const handleHeight = useCallback((element) => {
    setHeight(element.offsetHeight);
  }, []);

  const resetMenu = useCallback(() => {
    setMenu("index");
    setIsLeft(false);
  }, []);

  const handleLabelsBack = useCallback(() => {
    resetMenu();
    const currentLabels = labelsRef.current;
    const { oldLabels, newLabels } = currentLabels;
    if (typeof onChange === "function" && !isEqual(oldLabels.sort(), newLabels.sort())) {
      onChange({ labels: newLabels });
    }
  }, [onChange, resetMenu]);

  const handleItemClick = useCallback((value) => {
    setMenu(value);
    setIsLeft(true);
  }, []);

  const handleBack = useCallback(() => {
    const items = ["task_list", "assignee", "estimate", "date"];
    if (items.some((item) => item === menu)) {
      resetMenu();
    }
  }, [menu, resetMenu]);

  const change = useCallback(
    (data) => {
      setState((prevState) => ({ ...prevState, ...data }));
      if (typeof onChange === "function") {
        onChange(data);
      }
    },
    [onChange]
  );

  const handleChange = useCallback(
    (data) => {
      change(data);
      handleBack();
    },
    [handleBack, change]
  );

  const handleDateChange = useCallback(
    (data) => {
      change(data);
      if (selectDateMode === "atomic") {
        handleBack();
      }
    },
    [handleBack, change, selectDateMode]
  );

  const handleTaskListChange = useCallback(
    ({ task_list_id }) => {
      setTaskListId(task_list_id);
      taskListRef.current.newTaskListId = task_list_id;
      handleBack();
    },
    [handleBack]
  );

  const handleLabelsChange = useCallback((data) => {
    const { labels = [] } = data;
    setLabels(labels);
    labelsRef.current.newLabels = labels;
  }, []);

  useEffect(() => {
    setLabels(defaultLabels);
    labelsRef.current.oldLabels = defaultLabels;
  }, [defaultLabels]);

  useEffect(() => {
    const currentTaskList = taskListRef.current;
    const currentLabels = labelsRef.current;
    return () => {
      const { oldTaskListId, newTaskListId } = currentTaskList;
      const { oldLabels, newLabels } = currentLabels;

      if (typeof onChange === "function" && oldTaskListId !== newTaskListId) {
        onChange({ task_list_id: newTaskListId });
        Angie.logger.info(`Task list changed from ${oldTaskListId} to ${newTaskListId} for task ${id}`);
      }

      if (typeof onChange === "function" && !isEqual(oldLabels.sort(), newLabels.sort())) {
        onChange({ labels: newLabels });
      }
    };
  }, [id, onChange]);

  const handleEnteredIndex = useCallback(() => {
    setEnteredMenu("index");
  }, []);

  const handleEnteredDate = useCallback(() => {
    setEnteredMenu("date");
  }, []);

  const handleEnteredTaskList = useCallback(() => {
    setEnteredMenu("task_list");
  }, []);

  const handleEnteredAssignee = useCallback(() => {
    setEnteredMenu("assignee");
  }, []);

  const handleEnteredLabel = useCallback(() => {
    setEnteredMenu("label");
  }, []);

  const handleEnteredEstimate = useCallback(() => {
    setEnteredMenu("estimate");
  }, []);

  const handleHeightChange = useCallback(
    (value) => {
      const items = ["task_list", "assignee", "label"];
      if (items.some((item) => item === menu)) {
        setHeight(value + 6);
      }
    },
    [menu]
  );

  return (
    <ResizeTransition
      in
      style={{ height: `${height}px`, width }}
    >
      <div style={{ height: `${height}px`, width }}>
        <SlideLeftRightTransition
          in={menu === "index"}
          direction={isLeft ? "left" : "right"}
          onEnter={handleHeight}
          onEntered={handleEnteredIndex}
        >
          <MoveFocusInside disabled={enteredMenu !== "index"}>
            <StartMenu
              {...rest}
              id={id}
              projectId={projectId}
              canSeeEstimates={canSeeEstimates}
              onItemClick={handleItemClick}
              onChange={handleChange}
              hiddenFromClient={state.is_hidden_from_clients}
              important={state.is_important}
              style={{ width }}
              hideFromClients={hideFromClients}
              isLoggedUserClient={isLoggedUserClient}
              showTaskEstimatesToClients={showTaskEstimatesToClients}
            />
          </MoveFocusInside>
        </SlideLeftRightTransition>
        <SlideLeftRightTransition
          in={menu === "date"}
          direction={isLeft ? "left" : "right"}
          onEnter={handleHeight}
          onEntered={handleEnteredDate}
        >
          <MoveFocusInside disabled={enteredMenu !== "date"}>
            <Submenu
              title={t("Due Date")}
              onBack={resetMenu}
              style={{ width }}
            >
              <SelectTaskDate
                id={id}
                trigger="inline"
                assigneeId={assigneeId}
                onCancel={resetMenu}
                onChange={handleDateChange}
                selectedDays={state.dates}
                changeMode={selectDateMode}
              />
            </Submenu>
          </MoveFocusInside>
        </SlideLeftRightTransition>
        <SlideLeftRightTransition
          in={menu === "task_list"}
          direction={isLeft ? "left" : "right"}
          onEnter={handleHeight}
          onEntered={handleEnteredTaskList}
        >
          <Submenu
            title={t("Task List")}
            onBack={resetMenu}
            style={{ width }}
            onHeightChange={handleHeightChange}
          >
            <MoveFocusInside disabled={enteredMenu !== "task_list"}>
              <SelectTaskList
                selected={taskListId}
                onChange={handleTaskListChange}
                projectId={projectId}
              />
            </MoveFocusInside>
          </Submenu>
        </SlideLeftRightTransition>
        <SlideLeftRightTransition
          in={menu === "assignee"}
          direction={isLeft ? "left" : "right"}
          onEnter={handleHeight}
          onEntered={handleEnteredAssignee}
        >
          <Submenu
            title={t("Assignee")}
            onBack={resetMenu}
            style={{ width }}
            onHeightChange={handleHeightChange}
          >
            <MoveFocusInside disabled={enteredMenu !== "assignee"}>
              <SelectTaskAssignee
                selected={state.assignee_id}
                onChange={handleChange}
                disabledInternalSort
                hiddenFromClient={state.is_hidden_from_clients}
                projectId={projectId}
              />
            </MoveFocusInside>
          </Submenu>
        </SlideLeftRightTransition>
        <SlideLeftRightTransition
          in={menu === "label"}
          direction={isLeft ? "left" : "right"}
          onEnter={handleHeight}
          onEntered={handleEnteredLabel}
        >
          <Submenu
            title={t("Label")}
            onBack={handleLabelsBack}
            style={{ width }}
            onHeightChange={handleHeightChange}
          >
            <MoveFocusInside disabled={enteredMenu !== "label"}>
              <SelectTaskLabels
                projectId={projectId}
                selected={labels}
                onChange={handleLabelsChange}
              />
            </MoveFocusInside>
          </Submenu>
        </SlideLeftRightTransition>
        <SlideLeftRightTransition
          in={menu === "estimate"}
          direction={isLeft ? "left" : "right"}
          onEnter={handleHeight}
          onEntered={handleEnteredEstimate}
        >
          <Submenu
            title={t("Estimate")}
            onBack={resetMenu}
            style={{ width }}
          >
            <MoveFocusInside disabled={enteredMenu !== "estimate"}>
              <SelectTaskEstimate
                onCancel={resetMenu}
                estimate={state.estimate}
                jobTypeId={state.job_type_id}
                isSplit={isSplit}
                onChange={handleChange}
                onEscape={onEscape}
              />
            </MoveFocusInside>
          </Submenu>
        </SlideLeftRightTransition>
      </div>
    </ResizeTransition>
  );
};

TaskOptions.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taskListId: PropTypes.number,
  assigneeId: PropTypes.number,
  labels: PropTypes.arrayOf(PropTypes.number),
  estimate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobTypeId: PropTypes.number,
  hiddenFromClients: PropTypes.bool,
  important: PropTypes.bool,
  onChange: PropTypes.func,
  startOn: PropTypes.number,
  dueOn: PropTypes.number,
  initialHeight: PropTypes.number,
  selectDateMode: PropTypes.oneOf(["atomic", "instant"]),
  showTaskEstimatesToClients: PropTypes.bool,
  projectId: PropTypes.number,
  hideFromClients: PropTypes.bool,
  isLoggedUserClient: PropTypes.bool,
  isSplit: PropTypes.bool,
  onEscape: PropTypes.func,
};

TaskOptions.defaultProps = {
  id: 0,
  taskListId: 0,
  assigneeId: 0,
  labels: [],
  hiddenFromClients: false,
  important: false,
  initialHeight: 334,
  selectDateMode: "atomic",
  hideFromClients: false,
  isLoggedUserClient: false,
};
